
/**
 * The escape hatch for bespoke reports (SPEC §8.7).
 *
 * Most reports render generically from their manifest. For the occasional one
 * that needs custom presentation, map its slug to a React component here. The
 * generic renderer checks this map first and falls through to the declarative
 * path when there is no entry.
 *
 * The component receives the same §8.6 payload the generic renderer gets — it
 * does **not** get its own API endpoint, and it does not get a different
 * authorization path. Both of those would be a second way for a report to
 * reach data, and there is exactly one.
 *
 * Prefer extending the adapters where a report can be expressed declaratively:
 * every report that renders generically is one that keeps working when the
 * framework improves. But presentation variance is the norm rather than the
 * exception for the reports in §8.8, and forcing one of those through the
 * generic renderer produces a page with the right numbers and the wrong shape —
 * which is a worse outcome than an entry here.
 *
 * @example
 * ```ts
 * import { MarginBridge } from './MarginBridge'
 * export const CUSTOM_REPORTS: CustomReportMap = {
  'qa-inventory-exceptions': InventoryExceptions,
  'qa-item-specs': ItemSpecs,
 *   'finance-margin-bridge': MarginBridge,
 * }
 * ```
 */

import type { ComponentType } from 'react'

import type { ParamValues } from '../lib/params'
import type { ReportResult } from '../lib/types'

import { BreakDetectors } from './break-detectors/BreakDetectors'
import { CorrectiveActions } from './car/CorrectiveActions'
import { CustomerFeedback } from './customer-feedback/CustomerFeedback'
import { FinishedProductBatches } from './finished-product-batches/FinishedProductBatches'
import { FinishedProduct } from './finished-product/FinishedProduct'
import { GapScanner } from './gap-scanner/GapScanner'
import { IncomingProduct } from './incoming-product/IncomingProduct'
import { InventoryExceptions } from './inventory-exceptions/InventoryExceptions'
import { ItemSpecs } from './item-specs/ItemSpecs'
import { MonthEndForecast } from './month-end-forecast/MonthEndForecast'
import { QualityDashboard } from './quality-dashboard/QualityDashboard'
import { RepPerformance } from './rep-performance/RepPerformance'
import { SalesRecap } from './sales-recap/SalesRecap'
import { RightFirstTime } from './rft/RightFirstTime'
import { SpecExceptions } from './spec-exceptions/SpecExceptions'
import { Competitors } from './competitors/Competitors'
import { CustomerNews } from './customer-news/CustomerNews'
import { DrinkMenus } from './drink-menus/DrinkMenus'
import { MetroRanking } from './metro-ranking/MetroRanking'
import { ProductWatch } from './product-watch/ProductWatch'
import { ShipToDestinations } from './ship-to-destinations/ShipToDestinations'
import { TradeAreaMap } from './trade-area-map/TradeAreaMap'

export interface CustomReportProps {
  /** Exactly the payload the generic renderer received. */
  readonly result: ReportResult
  readonly department: string
  /**
   * The current parameter values, derived from the URL on every render exactly
   * as the generic path derives them. A custom report reads its filters from
   * here rather than holding its own copy — the acceptance property is that a
   * filtered URL reproduces the view, and it holds because there is nowhere
   * else for a filter to live.
   */
  readonly values: ParamValues
  /**
   * Write one parameter back to the URL. Goes through the same serialiser the
   * ParamBar uses, so a click in a custom panel and an edit in a picker cannot
   * produce two different URLs for the same view.
   */
  readonly setParam: (name: string, value: string) => void
}

export type CustomReportMap = Readonly<Record<string, ComponentType<CustomReportProps>>>

/**
 * Bespoke reports that render their own caveats (with `CaveatList`) rather than
 * have the route state them under the title. For a page whose notes are
 * background most readers do not need, at the foot is the honest placement;
 * the caveats are still on the page, never dropped. Every report not listed
 * keeps §9.6's default.
 */
export const CUSTOM_REPORTS_PLACING_CAVEATS: ReadonlySet<string> = new Set([
  'sales-month-end-forecast',
  'sales-rep-performance',
])

export const CUSTOM_REPORTS: CustomReportMap = {
  'qa-customer-feedback': CustomerFeedback,
  'qa-inventory-exceptions': InventoryExceptions,
  'qa-item-specs': ItemSpecs,
  'qa-quality-dashboard': QualityDashboard,
  'sales-break-detectors': BreakDetectors,
  'sales-gap-scanner': GapScanner,
  'sales-month-end-forecast': MonthEndForecast,
  'sales-competitors': Competitors,
  'sales-customer-news': CustomerNews,
  'sales-drink-menus': DrinkMenus,
  'sales-metro-ranking': MetroRanking,
  'sales-product-watch': ProductWatch,
  'sales-rep-performance': RepPerformance,
  'sales-recap': SalesRecap,
  'sales-ship-to-destinations': ShipToDestinations,
  'sales-trade-area-map': TradeAreaMap,
  'sc-corrective-actions': CorrectiveActions,
  'sc-finished-product-batches': FinishedProductBatches,
  'sc-finished-product': FinishedProduct,
  'sc-incoming-product': IncomingProduct,
  'sc-right-first-time': RightFirstTime,
  'sc-spec-exceptions': SpecExceptions,
}
